import { Head } from "@inertiajs/react"
import { CircleCheck, Ellipsis, Phone } from "lucide-react"
import { useState } from "react"

import { CallForwardingBanner } from "@/components/agents/dashboard/receptionist/call-forwarding-banner"
import { CallForwardingModal } from "@/components/agents/dashboard/receptionist/call-forwarding-modal"
import { PageHeadingBar, PageLayout } from "@/components/page-layout"
import { StatCard } from "@/components/partner-dashboard/stat-card"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

// Design mockup for the receptionist call-forwarding flow. Reproduces the real
// dashboard header (status, number, Edit, "…" menu) so the new banner and menu
// entry show in context, then opens the actual CallForwardingModal component.
// Everything below is hardcoded — open at /design/receptionist-call-forwarding.

const AGENT = {
  name: "AI Receptionist",
  phone_number: "1 (628) 555-0199",
}

const STATS = [
  { key: "calls", title: "Calls handled", value: 128, subLabel: "this month" },
  {
    key: "booked",
    title: "Appointments booked",
    value: 34,
    subLabel: "this month",
  },
  {
    key: "messages",
    title: "Messages taken",
    value: 61,
    subLabel: "this month",
  },
  { key: "minutes", title: "Minutes used", value: "412", subLabel: "of 600" },
]

const breadcrumbs: BreadcrumbItem[] = [
  { title: AGENT.name, href: "/design/receptionist-call-forwarding" },
]

export default function DesignReceptionistCallForwarding() {
  const [forwardingOpen, setForwardingOpen] = useState(false)
  // Local-only in the mock so a refresh always shows the banner (the real
  // dashboard persists dismissal in localStorage per-agent).
  const [bannerVisible, setBannerVisible] = useState(true)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Call forwarding (design)" />

      <CallForwardingModal
        open={forwardingOpen}
        onOpenChange={setForwardingOpen}
        receptionistNumber={AGENT.phone_number}
      />

      <PageLayout>
        {/* Header — mirrors the real receptionist dashboard */}
        <PageHeadingBar heading={AGENT.name}>
          <div className="flex flex-wrap gap-x-6 gap-y-4">
            <span className="flex items-center gap-3 text-sm/6">
              <CircleCheck className="size-5 shrink-0 fill-green-500 text-white" />
              <span className="font-medium text-green-700">Active</span>
            </span>
            <span className="flex items-center gap-3 text-sm/6">
              <Phone className="text-muted-foreground size-4 shrink-0 fill-current" />
              {AGENT.phone_number}
            </span>
          </div>
          <div className="flex gap-4">
            <Button variant="outline" size="sm" className="rounded-sm">
              Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-sm px-2"
                  aria-label="Agent actions"
                >
                  <Ellipsis className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setForwardingOpen(true)}>
                  Set up call forwarding
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>Pause</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive">
                  Cancel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </PageHeadingBar>

        {bannerVisible ? (
          <CallForwardingBanner
            onSetUp={() => setForwardingOpen(true)}
            onDismiss={() => setBannerVisible(false)}
          />
        ) : (
          <div className="text-muted-foreground flex items-center justify-between rounded-lg border border-dashed px-4 py-2 text-xs">
            <span>Banner dismissed — still reachable from the “…” menu.</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setBannerVisible(true)}
            >
              Restore banner
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {STATS.map((stat) => (
            <StatCard
              key={stat.key}
              title={stat.title}
              value={stat.value}
              subLabel={stat.subLabel}
              className="shadow-none"
            />
          ))}
        </div>

        {/* Placeholder body */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="text-muted-foreground flex h-48 items-center justify-center rounded-lg border border-dashed text-sm">
            Recent calls
          </div>
          <div className="text-muted-foreground flex h-48 items-center justify-center rounded-lg border border-dashed text-sm">
            Recent messages
          </div>
        </div>
      </PageLayout>
    </AppLayout>
  )
}
